import { mapActions, mapGetters } from "vuex";
import toast from "@/utils/toast";
import { cleanObject, formatDate } from "./utils/utils";

export default {
  name: "IdpEnrollmentReportMain",

  components: {},
  data() {
    return {
      filters: {
        page: 1,
        pageSize: 10,
        order: "desc",
        field: "updated_at",
        search: "",
        learning_type: "structured_learning",
        idp_period_id: null,
        all: false,
        nik: null,
        approval_step: null,
        status: null,
        training_scope: null,
        training_category: null,
        platform: null,
        training_method_id: null,
        training_type: null,
        coaching_type_id: null,
      },
      position: null,
      isLoading: false,
      isLoadingEmployeeList: false,
      isModalLoading: false,
      isLoadingSummary: false,
      _searchTimer: null,
      _employeeSearchTimer: null,
      _employeeListSearchTimer: null,
      employeeListSearch: "",
      employeeListPage: 1,
      employeeListPageSize: 10,
      showModal: false,
      modalData: null,
      idpPeriodOptions: [],
      activeFiltersCount: 0,
      selectedRows: [],
      isLoadingApprove: false,
      showConfirmationSubmitModal: false,
      showRejectConfirmationModal: false,
      isSubmitting: false,
      activeTab: "structured_learning",
      actionProject: {
        data: null,
        type: null,
      },
    };
  },

  computed: {
    ...mapGetters("idpEnrollment", [
      "structuredLearningDetail",
      "socialLearningDetail",
      "experientialLearningDetail",
      "idpLearningReport",
      "summaryIdpLearningReport",
      "trainingMethods",
      "trainingTypes",
      "competencies",
      "subordinates",
      "subordinatesMeta",
    ]),

    showingFrom() {
      if (!this.idpLearningReport.totalItems) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.idpLearningReport.totalItems ? this.idpLearningReport.totalItems : to;
    },

    selectedSubordinate() {
      return this.subordinates.find((sub) => String(sub.nik) === String(this.filters.nik)) || null;
    },

    selectedPeriodOption: {
      get() {
        return this.idpPeriodOptions.find((option) => String(option.value) === String(this.filters.idp_period_id)) || null;
      },
      set(option) {
        this.filters.idp_period_id = option?.value || null;
      },
    },

    selectedEmployeeName() {
      return this.selectedSubordinate?.name || "-";
    },

    pendingApprovalCount() {
      return this.showEmployeeList ? this.employeeListTotalSubmissions : this.idpLearningReport?.totalItems || 0;
    },

    showEmployeeList() {
      return !this.filters.nik;
    },

    employeeListTotalSubmissions() {
      return this.subordinates.reduce((total, item) => total + Number(item.total_learning || 0), 0);
    },

    employeeListTotalRecords() {
      return this.subordinatesMeta?.totalItems || this.subordinates.length || 0;
    },

    employeeListShowingFrom() {
      if (!this.employeeListTotalRecords) return 0;
      return (this.employeeListPage - 1) * this.employeeListPageSize + 1;
    },

    employeeListShowingTo() {
      const to = this.employeeListPage * this.employeeListPageSize;
      return to > this.employeeListTotalRecords ? this.employeeListTotalRecords : to;
    },

  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    async "filters.idp_period_id"() {
      this.filters.page = 1;

      await this.loadApprovalSubordinates(this.employeeListSearch, false);

      if (!this.filters.nik) {
        this.resetIdpLearnings();
        return;
      }

      this.loadSummaryLearning({ learningType: this.filters.learning_type });
      this.loadData();
    },

    "filters.nik"(newVal) {
      this.filters.page = 1;

      this.position = this.selectedSubordinate?.position || null;

      if (!newVal) {
        this.resetIdpLearnings();
        return;
      }

      this.loadData();

      this.loadSummaryLearning({ nik: newVal, learningType: this.filters.learning_type });
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("idpEnrollment", [
      "getIdpLearningReport",
      "getStructuredLearningDetail",
      "getSocialLearningDetail",
      "getExperientialLearningDetail",
      "getSummaryIdpLearningReport",
      "getSummaryIdpSocialLearningReport",
      "getSummaryIdpExperientialLearningReport",
      "getIdpPeriod",
      "getTrainingMethods",
      "getTrainingTypes",
      "getCompetencies",
      "getSubordinates",
      "resetIdpLearnings",
      "approveIdpLearning",
      "actionExperientialLearning",
    ]),

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        search: this.filters.search,
        all: this.filters.all,
        learning_type: this.filters.learning_type,
        idp_period_id: this.filters.idp_period_id,
        nik: this.filters.nik,
        type: "enrollment",
        reviewDetail: true,
      };

      return cleanObject(raw);
    },

    async loadData() {
      if (!this.filters.nik) {
        this.resetIdpLearnings();
        return;
      }

      try {
        this.isLoading = true;

        const query = this.buildQueryFromFilters();

        await this.getIdpLearningReport(query);

        if (this.idpLearningReport.page) this.filters.page = this.idpLearningReport.page;
        if (this.idpLearningReport.limit) this.filters.pageSize = this.idpLearningReport.limit;
      } finally {
        this.isLoading = false;
      }
    },

    async onTabChange(tab) {
      const isEmployeeDetail = Boolean(this.filters.nik);

      this.resetIdpLearnings();
      this.activeTab = tab;
      this.filters.learning_type = tab;
      this.modalData = null;
      this.filters.page = 1;
      this.selectedRows = [];
      this.resetFilters(false);

      if (isEmployeeDetail) {
        this.loadSummaryLearning({ learningType: tab });
        this.loadData();
        return;
      }

      this.position = null;
      await this.loadApprovalSubordinates(this.employeeListSearch, false);
    },

    onSelectChange({ selectedRows }) {
      this.selectedRows = [...selectedRows].filter((row) => row?.is_actionable !== false);
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onSortChange(params) {
      if (!Array.isArray(params) || params.length === 0) return;
      const { field, type } = params[0] || {};
      const isReset = type === "none";
      this.filters = {
        ...this.filters,
        field: isReset ? null : field,
        order: isReset ? null : type || "asc",
        page: 1,
      };
      this.loadData();
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = { ...this.filters, search: "", page: 1 };
      this.loadData();
    },

    updateUrlQuery(filters) {
      const query = cleanObject({
        ...this.$route.query,
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        search: filters.search,
        nik: filters.nik,
        idp_period_id: filters.idp_period_id,
        learning_type: filters.learning_type,
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => { });
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 10,
        order: query.order || null,
        field: query.field || null,
        search: query.search || "",
        nik: query.nik || null,
        idp_period_id: query.idp_period_id ? Number(query.idp_period_id) : this.filters.idp_period_id,
        learning_type: query.learning_type || this.filters.learning_type,
      };

      Object.keys(parsed).forEach((key) => {
        this.$set(this.filters, key, parsed[key]);
      });
    },

    handleApprove() {
      this.showConfirmationSubmitModal = true;
    },

    handleReject() {
      this.showRejectConfirmationModal = true;
    },

    async showFeedbackConfirmModal(data) {
      this.actionProject.data = data;
      this.actionProject.type = "feedback";
      this.$bvModal.show("action-project-modal");
    },

    async showApproveConfirmModal(data) {
      this.actionProject.data = data;
      this.actionProject.type = "approved";
      this.submitActionProject({ type: "approved", id: data.id });
    },

    async showRejectConfirmModal(data) {
      this.actionProject.data = data;
      this.actionProject.type = "rejected";
      this.$bvModal.show("action-project-modal");
    },

    async submitActionProject(payload) {
      let type = "";
      let action = "";

      switch (payload.type) {
        case "approved":
          type = "approve";
          action = "approve";
          break;
        case "rejected":
          type = "reject";
          action = "reject";
          break;
        case "feedback":
          type = "feedback";
          action = "approve";
          break;
      }

      const confirmMessages = {
        approve: "Apakah Anda yakin ingin menyetujui proyek ini?",
        reject: "Apakah Anda yakin ingin menolak proyek ini?",
        feedback: "Apakah Anda yakin ingin memberi umpan balik pada proyek ini?",
      };
      const successMessages = {
        approve: "Proyek berhasil disetujui",
        reject: "Proyek berhasil ditolak",
        feedback: "Umpan balik proyek berhasil dikirim",
      };

      this.$swal({
        title: "Apakah Anda yakin?",
        text: confirmMessages[type] || "Apakah Anda yakin ingin memproses proyek ini?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, kirim",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            this.isSubmitting = true;

            await this.actionExperientialLearning({ data: { ...payload, action } });

            toast({ type: "success", title: "Success", message: successMessages[type] || "Proyek berhasil diproses" });
            this.$bvModal.hide("action-project-modal");
            this.loadData();
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.isSubmitting = false;
          }
        }
      });
    },

    async handleViewDetail(row) {
      this.isModalLoading = true;
      this.selectedId = row.id;
      this.showModal = true;

      switch (this.filters.learning_type) {
        case "structured_learning":
          {
            await this.getStructuredLearningDetail({ id: row.id, query: { nik: this.filters.nik } });
          }
          break;
        case "social_learning":
          {
            await this.getSocialLearningDetail({ id: row.id, query: { nik: this.filters.nik } });
          }
          break;
        case "experiential_learning":
          {
            await this.getExperientialLearningDetail({ id: row.id, query: { nik: this.filters.nik } });
          }
          break;
        default:
      }

      this.$nextTick(() => {
        if (this.filters.learning_type === "structured_learning") this.$bvModal.show("structured-learning-modal");

        if (this.filters.learning_type === "social_learning") this.$bvModal.show("social-learning-modal");

        if (this.filters.learning_type === "experiential_learning") this.$bvModal.show("experiential-learning-modal");
      });
      this.isModalLoading = false;
    },

    async approveEnrollment(ids) {
      if (ids.length === 0) return;

      try {
        await this.approveIdpLearning({
          query: { learning_type: this.filters.learning_type },
          data: { nik: this.filters.nik, ids, action: 'approve', type: 'enrollment' },
        });

        toast({ type: "success", title: "Success", message: "Approve Pendaftaran berhasil!" });
        this.showConfirmationSubmitModal = false;
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";
        toast({ type: "error", title: "Error", message: msg });
      } finally {
        this.loadData();
      }
    },

    async rejectEnrollment(ids) {
      if (ids.length === 0) return;

      try {
        await this.approveIdpLearning({
          query: { learning_type: this.filters.learning_type },
          data: { nik: this.filters.nik, ids, action: 'reject', type: 'enrollment' },
        });

        toast({ type: "success", title: "Success", message: "Reject Pendaftaran berhasil!" });
        this.showRejectConfirmationModal = false;
      } catch (error) {
        const msg = error?.response?.data?.error || error?.message || "Terjadi kesalahan.";
        toast({ type: "error", title: "Error", message: msg });
      } finally {
        this.loadData();
      }
    },

    async loadSummaryLearning({ nik, learningType }) {
      const selectedNik = nik || this.filters.nik;
      const selectedLearningType = learningType || this.filters.learning_type;

      if (!selectedNik) {
        this.resetIdpLearnings();
        return;
      }

      this.isLoadingSummary = true;

      const payload = {
        learning_type: selectedLearningType,
        nik: selectedNik,
      };

      if (this.filters.idp_period_id) payload.idp_period_id = this.filters.idp_period_id;

      switch (selectedLearningType) {
        case "structured_learning":
          await this.getSummaryIdpLearningReport(payload);
          break;
        case "social_learning":
          await this.getSummaryIdpSocialLearningReport(payload);
          break;
        case "experiential_learning":
          await this.getSummaryIdpExperientialLearningReport(payload);
          break;
      }

      this.isLoadingSummary = false;
    },

    async loadIdpPeriod() {
      const idpPeriod = await this.getIdpPeriod();
      const now = new Date();

      this.filters.idp_period_id =
        idpPeriod.find((p) => new Date(p.start_date) <= now && new Date(p.end_date) >= now)?.id || null;

      this.idpPeriodOptions = idpPeriod.map((p) => ({
        text: this.formatPeriodLabel(p.start_date, p.end_date),
        value: p.id,
      }));
    },

    formatPeriodLabel(startDate, endDate) {
      if (!startDate || !endDate) return "";

      return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    },

    resetFilters(shouldLoad = true) {
      this.$set(this.filters, "page", 1);
      if (shouldLoad) this.reloadCurrentView();
    },

    reloadCurrentView() {
      if (this.showEmployeeList) {
        this.loadApprovalSubordinates(this.employeeListSearch, false);
        return;
      }
      this.loadData();
    },

    buildSubordinateQuery(search = "", approvalOnly = false) {
      return cleanObject({
        page: this.employeeListPage,
        pageSize: this.employeeListPageSize,
        all: false,
        field: "fullname",
        order: "asc",
        search,
        bibOnly: true,
        approvalOnly,
        countApprovalOnly: true,
        approvalType: "enrollment",
        learning_type: this.filters.learning_type,
        idp_period_id: this.filters.idp_period_id,
      });
    },

    async loadApprovalSubordinates(search = "", approvalOnly = false) {
      this.isLoadingEmployeeList = true;
      try {
        await this.getSubordinates(this.buildSubordinateQuery(search, approvalOnly));
        this.employeeListPage = this.subordinatesMeta?.page || this.employeeListPage;
        this.employeeListPageSize = this.subordinatesMeta?.limit || this.employeeListPageSize;
      } finally {
        this.isLoadingEmployeeList = false;
      }

      if (this.filters.nik && !this.selectedSubordinate && search === "") {
        const currentPage = this.employeeListPage;
        this.employeeListPage = 1;
        await this.getSubordinates(this.buildSubordinateQuery(this.filters.nik));
        this.employeeListPage = currentPage;
        if (!this.selectedSubordinate) this.filters.nik = null;
      }
    },

    selectApprovalEmployee(row) {
      if (!row?.nik) return;
      this.position = row.position || null;
      this.$router
        .push({
          query: cleanObject({
            ...this.$route.query,
            nik: row.nik,
            page: 1,
          }),
        })
        .catch(() => {});
    },

    backToEmployeeList() {
      this.position = null;
      this.selectedRows = [];
      this.resetIdpLearnings();
      const { nik, ...query } = this.$route.query;
      this.$router.replace({ query }).catch(() => {});
    },

    onEmployeeListSearch() {
      clearTimeout(this._employeeListSearchTimer);
      this._employeeListSearchTimer = setTimeout(() => {
        this.employeeListPage = 1;
        this.loadApprovalSubordinates(this.employeeListSearch, false);
      }, 350);
    },

    onEmployeeListPageChange(page) {
      this.employeeListPage = page;
      this.loadApprovalSubordinates(this.employeeListSearch, false);
    },

    onEmployeeListPerPageChange(pageSize) {
      this.employeeListPageSize = pageSize;
      this.employeeListPage = 1;
      this.loadApprovalSubordinates(this.employeeListSearch, false);
    },

    onEmployeeSearch(search, loading) {
      clearTimeout(this._employeeSearchTimer);
      const term = String(search || "").trim();

      if (term && term.length < 2) return;

      loading?.(true);
      this._employeeSearchTimer = setTimeout(async () => {
        try {
          await this.loadApprovalSubordinates(term, false);
        } finally {
          loading?.(false);
        }
      }, 350);
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();

    await Promise.all([
      this.loadIdpPeriod(),
      this.getTrainingMethods(),
      this.getTrainingTypes(),
      this.getCompetencies(),
    ]);

    await this.loadApprovalSubordinates("", false);
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
    clearTimeout(this._employeeSearchTimer);
    clearTimeout(this._employeeListSearchTimer);
    this.resetIdpLearnings();
  },
};
